import React from 'react';
import { useTranslation } from 'react-i18next';
import CommonSelect from '../CommonSelectAntd';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100]
}) => {
  const { t } = useTranslation();
  const totalPages = Math.ceil(totalItems / pageSize);
  
  // Calculate which page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if there are fewer than maxPagesToShow
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate middle pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        endPage = Math.min(totalPages - 1, 4);
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push('ellipsis-start');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  const paginationStyles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: '20px',
      marginBottom: '20px'
    },
    pageList: {
      display: 'flex',
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    pageItem: {
      margin: '0 4px',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: '32px',
      height: '32px',
      padding: '0 6px',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
      userSelect: 'none' as 'none',
      transition: 'all 0.3s ease',
      border: '1px solid #d9d9d9',
      backgroundColor: '#fff'
    },
    activePage: {
      borderColor: '#2c7be5',
      color: '#2c7be5',
      fontWeight: 500
    },
    ellipsis: {
      color: '#00000073',
      letterSpacing: '2px'
    },
    prevNext: {
      margin: '0 4px',
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: '32px',
      height: '32px',
      padding: '0 6px',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
      userSelect: 'none' as 'none',
      transition: 'all 0.3s ease',
      border: '1px solid #d9d9d9',
      backgroundColor: '#fff'
    },
    disabled: {
      color: '#00000040',
      borderColor: '#d9d9d9',
      cursor: 'not-allowed',
      backgroundColor: '#f5f5f5'
    },
    pageSizeSelector: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px'
    },
    select: {
      marginLeft: '8px',
      padding: '4px 8px',
      borderRadius: '4px',
      border: '1px solid #d9d9d9',
      backgroundColor: '#fff'
    },
    itemsText: {
      marginLeft: '16px',
      fontSize: '14px',
      color: '#00000073'
    }
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) {
      return;
    }
    onPageChange(page);
  };

  const handlePageSizeChange = (value: string) => {
    if (onPageSizeChange) {
      onPageSizeChange(Number(value));
    }
  };

  return (
    <div style={paginationStyles.container}>
      <div>
        <ul style={paginationStyles.pageList}>
          {/* Previous Button */}
          <li
            style={{
              ...paginationStyles.prevNext,
              ...(currentPage === 1 ? paginationStyles.disabled : {})
            }}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="ti ti-chevron-left"></i>
          </li>
          
          {/* Page Numbers */}
          {pageNumbers.map((page, index) => {
            if (page === 'ellipsis-start' || page === 'ellipsis-end') {
              return (
                <li
                  key={`ellipsis-${index}`}
                  style={{...paginationStyles.pageItem, ...paginationStyles.ellipsis}}
                >
                  •••
                </li>
              );
            }
            
            return (
              <li
                key={page}
                style={{
                  ...paginationStyles.pageItem,
                  ...(page === currentPage ? paginationStyles.activePage : {})
                }}
                onClick={() => handlePageChange(page as number)}
              >
                {page}
              </li>
            );
          })}
          
          {/* Next Button */}
          <li
            style={{
              ...paginationStyles.prevNext,
              ...(currentPage === totalPages ? paginationStyles.disabled : {})
            }}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="ti ti-chevron-right"></i>
          </li>
        </ul>
      </div>
      
      {onPageSizeChange && (
        <div style={paginationStyles.pageSizeSelector}>
          <span>{t('show')}</span>
          <CommonSelect
            className="ms-2"
            value={pageSize.toString()}
            onChange={handlePageSizeChange}
            options={pageSizeOptions.map((size) => ({
              value: size.toString(),
              label: `${size}`,
            }))}
          />
          <span style={paginationStyles.itemsText}>
            {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalItems)} {t('of')} {totalItems} {t('items')}
          </span>
        </div>
      )}
    </div>
  );
};

export default Pagination;
